import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { hodAPI } from '../../services/api';

const HODClaimReview = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [claim, setClaim] = useState(null);
    const [loading, setLoading] = useState(true);
    const [rejectionReason, setRejectionReason] = useState('');
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [recommendation, setRecommendation] = useState(null);

    useEffect(() => {
        fetchClaimDetails();
    }, [id]);

    const fetchClaimDetails = async () => {
        try {
            const response = await hodAPI.getClaimDetails(id);
            setClaim(response.data.claim);
        } catch (error) {
            console.error('Error fetching claim details:', error);
            alert('Failed to load claim details');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async () => {
        if (!recommendation) {
            alert('Please select Recommendation (Recommended/Not Recommended)');
            return;
        }
        if (!window.confirm('Are you sure you want to approve this claim?')) return;

        setProcessing(true);
        try {
            await hodAPI.approveClaim(id, { recommendation });
            alert('Claim approved successfully');
            navigate('/hod/dashboard');
        } catch (error) {
            console.error('Error approving claim:', error);
            alert('Failed to approve claim');
        } finally {
            setProcessing(false);
        }
    };

    const handleReject = async () => {
        if (!rejectionReason.trim()) {
            alert('Please provide a rejection reason');
            return;
        }

        setProcessing(true);
        try {
            await hodAPI.rejectClaim(id, rejectionReason, { recommendation });
            alert('Claim rejected successfully');
            navigate('/hod/dashboard');
        } catch (error) {
            console.error('Error rejecting claim:', error);
            alert('Failed to reject claim');
        } finally {
            setProcessing(false);
            setShowRejectModal(false);
        }
    };

    if (loading) return <div className="loading">Loading...</div>;
    if (!claim) return <div className="loading">Claim not found</div>;

    return (
        <div className="app-container">
            {/* Sidebar */}
            <div className="sidebar">
                <div style={{ marginBottom: '2rem' }}>
                    <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ffffff' }}>TRF System</h2>
                    <p style={{ fontSize: '0.875rem', color: '#ffffff' }}>HOD Portal</p>
                </div>

                <nav className="nav-menu">
                    <div className="nav-item">
                        <button
                            onClick={() => navigate('/hod/dashboard')}
                            className="nav-link"
                            style={{
                                background: 'transparent',
                                border: '1px solid #ffffff',
                                color: '#ffffff',
                                width: '100%',
                                justifyContent: 'center'
                            }}
                        >
                            ← Back to Claims
                        </button>
                    </div>
                </nav>

                <div style={{ marginTop: 'auto', paddingTop: '2rem', borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ marginBottom: '1rem' }}>
                        <p style={{ fontSize: '0.875rem', fontWeight: '600', color: '#ffffff' }}>{user.fullName}</p>
                        <p style={{ fontSize: '0.75rem', color: '#ffffff' }}>{user.email}</p>
                        <p style={{ fontSize: '0.75rem', color: '#ffffff', marginTop: '0.25rem' }}>{user.department}</p>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="main-content">
                <div className="header">
                    <h1 style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937' }}>
                        Review Claim
                    </h1>
                    <p style={{ color: '#6b7280' }}>{claim.claim_month} {claim.claim_year}</p>
                </div>

                {/* Scholar Information */}
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                    <div className="card-header"><h2 className="card-title">Scholar Information</h2></div>
                    <div className="card-body">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                            <div>
                                <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>Name</p>
                                <p style={{ fontWeight: '600' }}>{claim.scholarName || '-'}</p>
                            </div>
                            <div>
                                <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>TCE Roll No</p>
                                <p style={{ fontWeight: '600' }}>{claim.tceRollNo || '-'}</p>
                            </div>
                            <div>
                                <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>Department</p>
                                <p style={{ fontWeight: '600' }}>{claim.department || '-'}</p>
                            </div>
                            <div>
                                <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>Enrolled Month & Year</p>
                                <p style={{ fontWeight: '600' }}>{claim.enrolledMonthYear || '-'}</p>
                            </div>
                            <div>
                                <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>Email</p>
                                <p style={{ fontWeight: '600' }}>{claim.user_email}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Claim Details */}
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                    <div className="card-header"><h2 className="card-title">Claim Details</h2></div>
                    <div className="card-body">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                            <div>
                                <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>Claim Period</p>
                                <p style={{ fontWeight: '600' }}>{claim.claim_month} {claim.claim_year}</p>
                            </div>
                            <div>
                                <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>Claim Amount</p>
                                <p style={{ fontWeight: '700', fontSize: '1.25rem', color: '#059669' }}>
                                    ₹{claim.claim_amount?.toLocaleString()}
                                </p>
                            </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                            {claim.attendance_certificate && (
                                <div>
                                    <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Attendance Certificate</p>
                                    <a href={claim.attendance_certificate} target="_blank" rel="noopener noreferrer" className="btn btn-secondary" style={{ fontSize: '0.875rem' }}>
                                        View Certificate
                                    </a>
                                </div>
                            )}
                            {claim.progress_report && (
                                <div>
                                    <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Workload Report</p>
                                    <a href={claim.progress_report} target="_blank" rel="noopener noreferrer" className="btn btn-secondary" style={{ fontSize: '0.875rem' }}>
                                        View Report
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Leave Details & TRF Workload */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                    {claim.leave_details && (
                        <div className="card">
                            <div className="card-header"><h2 className="card-title">Leave Details</h2></div>
                            <div className="card-body">
                                <p>Casual Leave (CL): <strong>{claim.leave_details.cl ?? 0}</strong></p>
                                <p>Leave with Loss of Pay (LLP): <strong>{claim.leave_details.llp ?? 0}</strong></p>
                                <p>On Duty (OD): <strong>{claim.leave_details.od ?? 0}</strong></p>
                                <p>Balance CL: <strong>{claim.balCL ?? 0}</strong></p>
                            </div>
                        </div>
                    )}
                    {claim.trf_workload && (
                        <div className="card">
                            <div className="card-header"><h2 className="card-title">TRF Workload</h2></div>
                            <div className="card-body">
                                <p>Department Load: <strong>{claim.trf_workload.deptLoad ?? 0} hours</strong></p>
                                <p>Lab Hours: <strong>{claim.trf_workload.labHours ?? 0} hours</strong></p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Research Progress */}
                {claim.research_progress && (
                    <div className="card" style={{ marginBottom: '1.5rem' }}>
                        <div className="card-header"><h2 className="card-title">Research Progress</h2></div>
                        <div className="card-body">
                            <p style={{ marginBottom: '1rem' }}>
                                <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Description: </span>
                                {claim.research_progress.progressDescription || 'N/A'}
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <p style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Articles Published</p>
                                    <p>Journal: {claim.research_progress.articlesPublished?.journal ?? 0}</p>
                                    <p>Conference: {claim.research_progress.articlesPublished?.conference ?? 0}</p>
                                </div>
                                <div>
                                    <p style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Articles Submitted</p>
                                    <p>Journal: {claim.research_progress.articlesSubmitted?.journal ?? 0}</p>
                                    <p>Conference: {claim.research_progress.articlesSubmitted?.conference ?? 0}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Approval Chain Status */}
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                    <div className="card-header"><h2 className="card-title">Approval Chain Status</h2></div>
                    <div className="card-body">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '1rem' }}>
                            {[
                                { label: 'Supervisor', field: 'isSupervisorApproved' },
                                { label: 'DLC', field: 'isDlcApproved' },
                                { label: 'HOD', field: 'isHodApproved' },
                                { label: 'Admin', field: 'isadminApproved' }
                            ].map(({ label, field }) => {
                                const val = claim[field] || 'pending';
                                const color = val === 'approved' ? '#166534' : val === 'rejected' ? '#991b1b' : '#6b7280';
                                const bg = val === 'approved' ? '#dcfce7' : val === 'rejected' ? '#fee2e2' : '#f3f4f6';
                                return (
                                    <div key={field} style={{ textAlign: 'center', padding: '0.75rem', borderRadius: '0.5rem', background: bg }}>
                                        <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>{label}</p>
                                        <span style={{ fontWeight: '600', color, textTransform: 'capitalize' }}>{val}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Supervisor Remarks (Read-only for HOD) */}
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                    <div className="card-header">
                        <h2 className="card-title">Supervisor Remarks</h2>
                    </div>
                    <div className="card-body">
                        <div>
                            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Research Progress</p>
                            <p style={{ fontWeight: '600', color: claim['SupervisorRemarks(ResearchProgress)'] === 'Satisfactory' ? '#059669' : '#ef4444' }}>
                                {claim['SupervisorRemarks(ResearchProgress)'] || 'Not recorded'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* HoD Remarks Section */}
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                    <div className="card-header">
                        <h2 className="card-title">HoD Remarks</h2>
                    </div>
                    <div className="card-body">
                        <div style={{ marginBottom: '1rem' }}>
                            <p style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Recommendation <span style={{ color: '#ef4444' }}>*</span></p>
                            <div style={{ display: 'flex', gap: '2rem' }}>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '0.5rem' }}>
                                    <input
                                        type="radio"
                                        name="recommendation"
                                        value="Recommended"
                                        checked={recommendation === 'Recommended'}
                                        onChange={(e) => setRecommendation(e.target.value)}
                                        style={{ width: '1.25rem', height: '1.25rem' }}
                                    />
                                    <span>Recommended</span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '0.5rem' }}>
                                    <input
                                        type="radio"
                                        name="recommendation"
                                        value="Not Recommended"
                                        checked={recommendation === 'Not Recommended'}
                                        onChange={(e) => setRecommendation(e.target.value)}
                                        style={{ width: '1.25rem', height: '1.25rem' }}
                                    />
                                    <span>Not Recommended</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Resubmission Remarks */}
                {claim.resubmission_remarks && (
                    <div className="card" style={{ marginBottom: '1.5rem', borderLeft: '4px solid #f59e0b' }}>
                        <div className="card-header">
                            <h2 className="card-title">Resubmission Remarks</h2>
                        </div>
                        <div className="card-body">
                            <p>{claim.resubmission_remarks}</p>
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="card">
                    <div className="card-body">
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowRejectModal(true)}
                                className="btn btn-danger"
                                disabled={processing}
                            >
                                Reject
                            </button>
                            <button
                                onClick={handleApprove}
                                className="btn btn-success"
                                disabled={processing}
                            >
                                {processing ? 'Processing...' : 'Approve'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Reject Modal */}
            {showRejectModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div className="card" style={{ width: '500px', maxWidth: '90%' }}>
                        <div className="card-header">
                            <h2 className="card-title">Reject Claim</h2>
                        </div>
                        <div className="card-body">
                            <p style={{ marginBottom: '1rem', color: '#6b7280' }}>
                                Please provide a reason for rejecting this claim:
                            </p>
                            <textarea
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                placeholder="Enter rejection reason..."
                                rows="4"
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem'
                                }}
                            />
                            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                                <button
                                    onClick={() => {
                                        setShowRejectModal(false);
                                        setRejectionReason('');
                                    }}
                                    className="btn btn-secondary"
                                    disabled={processing}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleReject}
                                    className="btn btn-danger"
                                    disabled={processing}
                                >
                                    {processing ? 'Processing...' : 'Confirm Rejection'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HODClaimReview;
